import { useCallback } from "react";
import { message } from "antd";
import { CopyOutlined } from "@ant-design/icons";
import copyToClipboard from "../../utils/copy";
import { useTranslation } from "../../i18n";
import { useWhiteLabel } from "../../whiteLabel/useWhiteLabel";

const AVATAR_SRC = `${process.env.PUBLIC_URL || ""}/social/mr-hei-avatar.jpg`;

function XiaohongshuLogo() {
    return (
        <svg viewBox="0 0 24 24" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <rect width="24" height="24" rx="6" fill="#FF2741" />
            <text
                x="12"
                y="12.5"
                fill="#FFFFFF"
                fontSize="7.5"
                fontWeight="700"
                textAnchor="middle"
                dominantBaseline="central"
                fontFamily="-apple-system, 'PingFang SC', 'Microsoft YaHei', sans-serif"
            >
                小红书
            </text>
        </svg>
    );
}

function InstagramLogo() {
    return (
        <svg viewBox="0 0 24 24" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <defs>
                <radialGradient id="ig-badge-grad" cx="0.3" cy="1.1" r="1.3">
                    <stop offset="0" stopColor="#fdf497" />
                    <stop offset="0.45" stopColor="#fd5949" />
                    <stop offset="0.6" stopColor="#d6249f" />
                    <stop offset="0.9" stopColor="#285aeb" />
                </radialGradient>
            </defs>
            <rect width="24" height="24" rx="6" fill="url(#ig-badge-grad)" />
            <g fill="none" stroke="#FFFFFF" strokeWidth="2">
                <rect x="5" y="5" width="14" height="14" rx="4.5" />
                <circle cx="12" cy="12" r="3.4" />
            </g>
            <circle cx="16.6" cy="7.4" r="1.2" fill="#FFFFFF" />
        </svg>
    );
}

function DouyinLogo() {
    return (
        <svg viewBox="0 0 24 24" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <rect width="24" height="24" rx="6" fill="#010101" />
            <path
                fill="#25F4EE"
                d="M9.7 8.2v8.1a2.33 2.33 0 0 1-2.33 2.25c-.29 0-.56-.05-.81-.14a2.33 2.33 0 0 0 1.71.75 2.33 2.33 0 0 0 2.33-2.25V8.83c.34.2.7.36 1.06.49V8.13s-1.27-.05-1.96-.06z"
            />
            <path
                fill="#FE2C55"
                d="M16.1 6.74c-.6-.38-1.05-.95-1.32-1.61.16.87.66 1.63 1.38 2.12v1.86a6.6 6.6 0 0 1-2.78-.83v5.71c0 2.83-2.3 5.13-5.12 5.13-.94 0-1.83-.26-2.58-.7a5.11 5.11 0 0 0 3.63 1.52c2.83 0 5.12-2.3 5.12-5.13V8.85a6.6 6.6 0 0 0 3.87 1.24V7.57c-.83 0-1.6-.31-2.2-.83z"
            />
            <path
                fill="#FFFFFF"
                d="M13.39 13.99V8.28c.81.49 1.74.79 2.78.83V7.25a4.28 4.28 0 0 1-1.38-2.12h-1.96v11.16a2.33 2.33 0 0 1-2.33 2.25 2.33 2.33 0 0 1-1.71-.75 2.33 2.33 0 0 1-1.49-2.18c0-1.29 1.05-2.34 2.34-2.34.25 0 .5.04.73.12v-2.56a5.13 5.13 0 0 0-.73-.05c-2.83 0-5.12 2.3-5.12 5.13 0 1.74.86 3.27 2.18 4.2.75.44 1.64.7 2.58.7 2.83 0 4.84-2.3 4.84-4.83z"
            />
        </svg>
    );
}

const SOCIALS = [
    {
        key: "xiaohongshu",
        platform: "小红书",
        username: "伦敦摄影师小黑",
        link: "https://www.xiaohongshu.com/user/profile/60cde6860000000001007032?xhsshare=CopyLink&appuid=5e440cdb00000000010086f7&apptime=1728245844",
        copyText: "伦敦摄影师小黑",
        logo: <XiaohongshuLogo />,
    },
    {
        key: "instagram",
        platform: "Instagram",
        username: "mr.hei_",
        link: "https://www.instagram.com/mr.hei_",
        copyText: "mr.hei_",
        logo: <InstagramLogo />,
    },
    {
        key: "douyin",
        platform: "抖音",
        username: "伦敦摄影师小黑",
        link: "https://v.douyin.com/tpLvmgxHrRY/",
        copyText: "1- 长按复制此条消息，打开抖音搜索，查看TA的更多作品。 https://v.douyin.com/tpLvmgxHrRY/ 1@3.com :7pm",
        logo: <DouyinLogo />,
    },
];

function SocialMediaCards({ consentNs = "delivery.consent" }) {
    const { t } = useTranslation();
    const { isWhiteLabel } = useWhiteLabel();

    const handleOpen = useCallback((link) => {
        window.open(link, "_blank", "noopener,noreferrer");
    }, []);

    const handleCopy = useCallback(
        async (event, text) => {
            event.stopPropagation();
            try {
                await copyToClipboard(text);
                message.success(t(`${consentNs}.social.copied`));
            } catch {
                message.error(t(`${consentNs}.social.copyFailed`));
            }
        },
        [consentNs, t]
    );

    if (isWhiteLabel) {
        return null;
    }

    return (
        <div className="delivery-social">
            <p className="delivery-social__title">{t(`${consentNs}.social.title`)}</p>
            <div className="delivery-social__cards">
                {SOCIALS.map((social) => (
                    <div
                        key={social.key}
                        className="delivery-social-card"
                        role="link"
                        tabIndex={0}
                        onClick={() => handleOpen(social.link)}
                        onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                handleOpen(social.link);
                            }
                        }}
                    >
                        <span className="delivery-social-card__avatar">
                            <img
                                className="delivery-social-card__photo"
                                src={AVATAR_SRC}
                                alt={`${social.platform} ${social.username}`}
                                loading="lazy"
                            />
                            <span className="delivery-social-card__badge">{social.logo}</span>
                        </span>
                        <span className="delivery-social-card__info">
                            <span className="delivery-social-card__username">{social.username}</span>
                            <span className="delivery-social-card__platform">{social.platform}</span>
                        </span>
                        <button
                            type="button"
                            className="delivery-social-card__copy"
                            onClick={(event) => handleCopy(event, social.copyText)}
                        >
                            <CopyOutlined />
                            <span>{t(`${consentNs}.social.copy`)}</span>
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default SocialMediaCards;
